import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Container } from '@ui';
import { convertHexToRGBA } from '@utils/usefulFuncs';
import { isUndefinedOrNull, logNetRequest } from '@utils/usefulFuncs';
import { SelectableDocumentsList } from '@containers/documents/SelectableDocumentsList';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { Loading } from '@ui/Loading';
import { assembleDocumentTypes, filterDocsByReportingFilter } from '@utils/doc';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { ROUTES } from '@constants/routes';
import { useNavigate } from 'react-router';
import {
  assembleReportingPeriodTypes,
  REPORTING_PERIOD_AVAILABLE_TYPES,
  ReportingPeriodFilter,
} from './ReportingPeriodFilter';

const Wrapper = styled.div``;
const TickerWrapper = styled.div`
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.themeColors.text};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.14s;
  min-width: 66px;
  text-align: center;

  &:hover {
    background: ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.1)};
    border: 1px solid ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 1)};

    * {
      color: ${({ theme }) => theme.themeColors.primaryCtaButton};
    }
  }

  &.selected {
    border: 1px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    background: ${({ theme }) => theme.themeColors.primaryCtaButton};

    * {
      color: ${({ theme }) => theme.themeColors.buttonText};
    }
  }
`;
const TickersButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
`;

const noneType = 'None';

function isDocumentTypeActive(documentType, activeDocumentType) {
  return documentType === activeDocumentType;
}

const DocumentTypeButton = ({ value, activeDocumentType, onClick }) => {
  return (
    <TickerWrapper
      onClick={() => onClick(value)}
      className={isDocumentTypeActive(value, activeDocumentType) ? 'selected' : ''}
    >
      <Body5 bold>{value === 'unknown' || isUndefinedOrNull(value) ? 'Other Materials' : value}</Body5>
    </TickerWrapper>
  );
};

export const MyUploadedDocumentSelection = ({
  selectedDocs,
  handleAddDocument: onSelect,
  handleRemoveDocument: onRemoveSelect,
  singleDocMode,
}) => {
  const navigate = useNavigate();
  const [documentTypes, setDocumentTypes] = React.useState([]);
  const [activeDocumentType, setActiveDocumentType] = React.useState(null);
  const [docs, setDocs] = React.useState(null);
  const [filteredDocs, setFilteredDocs] = React.useState([]);

  const [reportingPeriodFilterValues, setReportingPeriodFilterValues] = React.useState({});
  const [reportingPeriodTypes, setReportingPeriodTypes] = React.useState([]);
  console.log(
    'reportingPeriodTypes',
    REPORTING_PERIOD_AVAILABLE_TYPES,
    reportingPeriodTypes,
    reportingPeriodFilterValues
  );

  React.useEffect(() => {
    const fetchDocs = async () => {
      const URL = 'v1/genai/documents?category=user';
      const { status, data, error } = await sendApiRequest('get', URL);
      handle400Statuses(status);
      logNetRequest(status, data, error);
      if (status === 200) {
        const docs = data.documents;
        setDocs(docs);
        setDocumentTypes(assembleDocumentTypes(docs));
        const nextReportingPeriodTypes = assembleReportingPeriodTypes(docs);
        console.log('nextReportingPeriodTypes', nextReportingPeriodTypes, docs);
        setReportingPeriodTypes(nextReportingPeriodTypes);
        setReportingPeriodFilterValues({ quarter: '', month: '', year: '' });
        setFilteredDocs(docs);
      } else {
        console.error('Error fetching docs', error);
      }
    };
    fetchDocs();
  }, []);

  const uploadNewDocumentsClick = () => {
    const url = ROUTES.docManagement;
    navigate(url);
  };

  const handleDocumentTypeClick = (d) => {
    if (d === activeDocumentType) {
      setActiveDocumentType(null);
      setFilteredDocs(docs);
      return;
    }

    setActiveDocumentType(d);
    const filtered =
      d === 'CSV'
        ? docs.filter((doc) => doc.content_type === 'text/csv')
        : docs.filter((doc) => doc.doc_type === d);
    setFilteredDocs(filtered);
  };

  const handleReportingPeriodChange = (type, value) => {
    const nextFilters = { ...reportingPeriodFilterValues, [type]: value };
    setReportingPeriodFilterValues(nextFilters);
    const filteredDocs = filterDocsByReportingFilter(docs, nextFilters);
    setFilteredDocs(filteredDocs);
  };

  if (!docs) {
    return (
      <Wrapper>
        <Loading />
      </Wrapper>
    );
  }

  if (docs.length === 0) {
    return (
      <Wrapper>
        <Container centerAll top={72} bottom={96}>
          <Body1>No documents found. Upload a document to get started.</Body1>
        </Container>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Container top={12}>
        <SkeletonButton onClick={uploadNewDocumentsClick}>Upload New Documents</SkeletonButton>
      </Container>
      <Container top={24}>
        <Container bottom={12}>
          <Body5>Document Types</Body5>
        </Container>
        <TickersButtonWrapper>
          {documentTypes.map((dt) => (
            <DocumentTypeButton
              key={dt}
              value={dt}
              activeDocumentType={activeDocumentType}
              onClick={handleDocumentTypeClick}
            />
          ))}
        </TickersButtonWrapper>
      </Container>
      {Object.values(reportingPeriodTypes).some((v) => v.length > 0) ? (
        <Container top={24}>
          <Container bottom={12}>
            <Body5>Reporting Periods</Body5>
          </Container>
          <TickersButtonWrapper>
            {REPORTING_PERIOD_AVAILABLE_TYPES.map((reportingPeriodType) =>
              reportingPeriodTypes[reportingPeriodType]?.length > 0 ? (
                <ReportingPeriodFilter
                  key={reportingPeriodType}
                  reportingPeriodType={reportingPeriodType}
                  options={reportingPeriodTypes[reportingPeriodType]}
                  value={reportingPeriodFilterValues[reportingPeriodType] || noneType}
                  onChange={handleReportingPeriodChange}
                />
              ) : null
            )}
          </TickersButtonWrapper>
        </Container>
      ) : null}
      <Container top={24}>
        <SelectableDocumentsList
          docs={filteredDocs}
          selectedDocs={selectedDocs}
          onSelect={onSelect}
          onRemoveSelect={onRemoveSelect}
          singleDocMode={singleDocMode}
        />
      </Container>
    </Wrapper>
  );
};
